import { returnData, type HttpClient } from 'services/api-client/client';
import type {
  CreateProjectPayload,
  Project,
  ProjectWithSubProjectIndicator,
  ProjectWithoutChildren,
  SlimProject,
  UpdateProjectPayload
} from 'types/api.types';
import type { AxiosResponse, RawAxiosResponseHeaders } from 'axios';
import type { RolesApi } from '@env0/role-service/api';
import type { ProjectApi } from '@env0/project-service/api';

export default (client: HttpClient) => ({
  findByOrganizationId: (organizationId: string) =>
    client.get<ProjectWithoutChildren[]>('projects', {
      params: {
        organizationId
      }
    }),
  findTreeByOrganizationId: (organizationId: string) =>
    client.get<SlimProject[]>('projects/tree', {
      params: {
        organizationId
      }
    }),
  findChildren: ({
    organizationId,
    parentProjectId
  }: {
    organizationId: string;
    parentProjectId?: string;
  }): Promise<ProjectWithSubProjectIndicator[]> =>
    client
      .get<ProjectWithSubProjectIndicator[]>('projects/children', { params: { organizationId, parentProjectId } })
      .then(returnData),
  searchByOrganizationId: ({
    organizationId,
    search,
    limit,
    nextToken
  }: {
    organizationId: string;
    search: string;
    limit: number;
    nextToken?: string;
  }): Promise<{
    data: ProjectWithoutChildren[];
    headers?: RawAxiosResponseHeaders & { 'x-has-more-pages'?: string };
  }> =>
    client
      .get<ProjectWithoutChildren[]>('projects', {
        params: { organizationId, search, limit, nextToken, isArchived: 'false' }
      })
      .then(response => ({ data: response.data, headers: response.headers })),
  getById: (projectId: string) =>
    client.get<Project>(`projects/${projectId}`, { hideNotification: true }).then(returnData),
  create: (payload: CreateProjectPayload) =>
    client.post<CreateProjectPayload, AxiosResponse<ProjectWithoutChildren>>('projects', payload),
  update: (projectId: string, payload: UpdateProjectPayload) =>
    client.put<UpdateProjectPayload, AxiosResponse<ProjectWithoutChildren>>(`projects/${projectId}`, payload),
  archive: (projectId: string) => client.delete(`projects/${projectId}`),
  move: (projectId: string, targetProjectId: string | null) =>
    client.post(`projects/${projectId}/move`, { targetProjectId }),
  getUsers: (projectId: string) => client.get<RolesApi.UserRoleAssignment[]>(`permissions/projects/${projectId}`),
  removeUser: (projectId: string, userId: string) =>
    client.delete(`permissions/projects/${projectId}/users/${encodeURI(userId)}`),
  assignUser: (projectId: string, userId: string, role: RolesApi.RBACPermissionRole) =>
    client.post<{ userId: string; role: RolesApi.RBACPermissionRole }, AxiosResponse<RolesApi.UserRoleAssignment>>(
      `permissions/projects/${projectId}`,
      {
        userId,
        role
      }
    ),
  updateUser: (projectId: string, userId: string, role: RolesApi.RBACPermissionRole) =>
    client.put<{ role: RolesApi.RBACPermissionRole }, AxiosResponse<RolesApi.UserRoleAssignment>>(
      `permissions/projects/${projectId}/users/${encodeURI(userId)}`,
      {
        role
      }
    ),
  getModulesTestingProject: (organizationId: string) =>
    client
      .get<ProjectApi.GetModulesTestingProject.Response>(`projects/modules/testing/${organizationId}`, {
        hideNotification: true
      })
      .then(returnData),
  findTagsByOrganizationId: (organizationId: string) =>
    client
      .get<ProjectApi.FindTagsByOrganizationId.Response>('projects/tags', { params: { organizationId } })
      .then(returnData)
});
